import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, ChevronDown, Sparkles, HelpCircle } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

const hexA = (hex, a) => {
  const h = (hex || "#d04260").replace("#", "");
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};

const HighlightCard = ({ h, i, accent, fromLeft }) => (
  <motion.div
    initial={{ opacity: 0, x: fromLeft ? -40 : 40, rotate: fromLeft ? -1.5 : 1.5 }}
    whileInView={{ opacity: 1, x: 0, rotate: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.5, delay: Math.min(i * 0.05, 0.35), ease: [0.22, 1, 0.36, 1] }}
    whileHover={{ y: -5 }}
    className="relative rounded-2xl p-4 pl-5 bg-white/80 backdrop-blur-md shadow-sm hover:shadow-xl transition-shadow"
    style={{ border: `1px solid ${hexA(accent, 0.16)}`, borderLeft: `4px solid ${accent}` }}
    data-testid={`city-highlight-${i}`}
  >
    <div className="flex items-start gap-3">
      <motion.div
        whileHover={{ scale: 1.12, rotate: 6 }}
        transition={{ type: "spring", stiffness: 300 }}
        className="shrink-0 w-10 h-10 rounded-2xl flex items-center justify-center text-base font-black text-white shadow-md"
        style={{ background: `linear-gradient(135deg, ${accent} 0%, ${hexA(accent, 0.7)} 100%)` }}
      >
        {i + 1}
      </motion.div>
      <div className="min-w-0 pt-0.5">
        <h3 className="text-sm md:text-base font-black text-zinc-900 leading-snug">{h.title}</h3>
        <p className="mt-1 text-xs md:text-sm text-zinc-500 leading-relaxed">{h.text}</p>
      </div>
    </div>
  </motion.div>
);

export const CityGuide = ({ guide, cityName, accent = "#d04260", fontFamily = "Inter" }) => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(null);
  if (!guide || !cityName) return null;
  const highlights = guide.highlights || [];
  const faqs = guide.faqs || [];
  if (highlights.length === 0) return null;

  const left = highlights.filter((_, i) => i % 2 === 0);
  const right = highlights.filter((_, i) => i % 2 === 1);

  const faqJsonLd = faqs.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.question,
          acceptedAnswer: { "@type": "Answer", text: f.answer },
        })),
      }
    : null;

  return (
    <motion.section
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.5 }}
      className="mt-5 md:mt-6 rounded-3xl p-5 md:p-8 relative overflow-hidden"
      style={{ background: `linear-gradient(160deg, ${hexA(accent, 0.03)} 0%, ${hexA(accent, 0.09)} 100%)`, border: `1px solid ${hexA(accent, 0.2)}` }}
      data-testid="city-guide-section"
    >
      {faqJsonLd && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />
      )}
      {/* Decorative animated orbs */}
      <motion.div
        animate={{ x: [0, 26, 0], y: [0, -18, 0], scale: [1, 1.12, 1] }}
        transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -top-20 -right-16 w-64 h-64 rounded-full pointer-events-none"
        style={{ background: `radial-gradient(circle, ${hexA(accent, 0.14)}, transparent 70%)` }}
      />
      <motion.div
        animate={{ x: [0, -22, 0], y: [0, 16, 0], scale: [1, 1.1, 1] }}
        transition={{ duration: 18, repeat: Infinity, ease: "easeInOut", delay: 2 }}
        className="absolute -bottom-24 -left-16 w-64 h-64 rounded-full pointer-events-none"
        style={{ background: `radial-gradient(circle, ${hexA(accent, 0.09)}, transparent 70%)` }}
      />

      <div className="relative">
        {/* Header */}
        <motion.p
          initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="text-[11px] font-bold uppercase tracking-[0.18em] mb-2 inline-flex items-center gap-1.5" style={{ color: accent }}
        >
          <MapPin className="w-3.5 h-3.5" /> {t("cityguide.eyebrow")}
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.05 }}
          className="text-2xl md:text-4xl font-black tracking-tight text-zinc-900 leading-tight" style={{ fontFamily }} data-testid="city-guide-title"
        >
          {t("cityguide.title")} {cityName}
        </motion.h2>
        <p className="mt-1.5 text-sm md:text-base font-bold" style={{ color: accent }}>{t("cityguide.subtitle")}</p>
        {guide.intro && <p className="mt-3 text-sm md:text-base text-zinc-500 max-w-2xl leading-relaxed">{guide.intro}</p>}

        {/* Highlights — banner sfasati (colonna destra offset verticale) */}
        <div className="mt-7 grid sm:grid-cols-2 gap-4 items-start">
          <div className="flex flex-col gap-4">
            {left.map((h, k) => (
              <HighlightCard key={k} h={h} i={k * 2} accent={accent} fromLeft />
            ))}
          </div>
          <div className="flex flex-col gap-4 sm:mt-10">
            {right.map((h, k) => (
              <HighlightCard key={k} h={h} i={k * 2 + 1} accent={accent} fromLeft={false} />
            ))}
          </div>
        </div>

        {/* FAQ accordion */}
        {faqs.length > 0 && (
          <div className="mt-9">
            <p className="text-sm md:text-base font-black text-zinc-900 inline-flex items-center gap-2 mb-3">
              <HelpCircle className="w-4 h-4" style={{ color: accent }} /> {t("cityguide.faq_title")} {cityName}
            </p>
            <div className="rounded-2xl overflow-hidden bg-white/80 backdrop-blur-md shadow-sm" style={{ border: `1px solid ${hexA(accent, 0.16)}` }}>
              {faqs.map((f, i) => {
                const isOpen = open === i;
                return (
                  <div key={i} className={i > 0 ? "border-t border-zinc-100" : ""}>
                    <button
                      onClick={() => setOpen(isOpen ? null : i)}
                      className="w-full flex items-center justify-between gap-3 text-left px-4 md:px-5 py-3.5 hover:bg-black/[0.02] transition-colors"
                      data-testid={`city-faq-toggle-${i}`}
                      aria-expanded={isOpen}
                    >
                      <span className="text-sm md:text-[15px] font-bold text-zinc-800">{f.question}</span>
                      <motion.span animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.2 }} className="shrink-0">
                        <ChevronDown className="w-4 h-4" style={{ color: accent }} />
                      </motion.span>
                    </button>
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.25 }}
                          className="overflow-hidden"
                        >
                          <p className="px-4 md:px-5 pb-4 text-sm text-zinc-600 leading-relaxed" data-testid={`city-faq-answer-${i}`}>
                            {f.answer}
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <p className="mt-5 text-[10px] text-zinc-400 inline-flex items-center gap-1">
          <Sparkles className="w-3 h-3" /> {t("cityguide.ai_note")}
        </p>
      </div>
    </motion.section>
  );
};
